import { CALL_API, BASE } from 'store/middleware/api';
import { joinPath } from 'utils/string';
import { getCancelPreview } from 'app/AccountNew/InvestorOutreach/investorOutreachApi';

import { INVESTOR_SEARCH_ENDPOINT } from './constants';
import { toSearchRequest } from './investorUtils';

// Subject-property suggestions. Same endpoint as the main search, but its own
// action types so the results land in the investors slice and the two surfaces
// never share selection state (S4 build-spec §2).
export const SEARCH_SUBJECT_SUGGESTIONS = 'SEARCH_SUBJECT_SUGGESTIONS';
export const SEARCH_SUBJECT_SUGGESTIONS_SUCCESS = 'SEARCH_SUBJECT_SUGGESTIONS_SUCCESS';
export const SEARCH_SUBJECT_SUGGESTIONS_ERROR = 'SEARCH_SUBJECT_SUGGESTIONS_ERROR';

export const SET_SUBJECT_LABEL = 'SET_SUBJECT_LABEL';
export const SET_SUBJECT = 'SET_SUBJECT';
export const CLEAR_SUBJECT = 'CLEAR_SUBJECT';
export const SET_RECENT_SUBJECTS = 'SET_RECENT_SUBJECTS';

export const SET_INVESTOR_FILTER = 'SET_INVESTOR_FILTER';
export const RESET_INVESTOR_FILTERS = 'RESET_INVESTOR_FILTERS';

export const CLEAR_INVESTOR_RESULTS = 'CLEAR_INVESTOR_RESULTS';
export const SEARCH_INVESTORS = 'SEARCH_INVESTORS';
export const SEARCH_INVESTORS_SUCCESS = 'SEARCH_INVESTORS_SUCCESS';
export const SEARCH_INVESTORS_ERROR = 'SEARCH_INVESTORS_ERROR';

export const SAVE_INVESTOR_GROUP = 'SAVE_INVESTOR_GROUP';
export const SAVE_INVESTOR_GROUP_SUCCESS = 'SAVE_INVESTOR_GROUP_SUCCESS';
export const SAVE_INVESTOR_GROUP_ERROR = 'SAVE_INVESTOR_GROUP_ERROR';

export const REMOVE_INVESTOR_FROM_GROUP = 'REMOVE_INVESTOR_FROM_GROUP';
export const REMOVE_INVESTOR_FROM_GROUP_SUCCESS = 'REMOVE_INVESTOR_FROM_GROUP_SUCCESS';
export const REMOVE_INVESTOR_FROM_GROUP_ERROR = 'REMOVE_INVESTOR_FROM_GROUP_ERROR';

export const MATERIALIZE_INVESTOR_CONTACTS = 'MATERIALIZE_INVESTOR_CONTACTS';
export const MATERIALIZE_INVESTOR_CONTACTS_SUCCESS = 'MATERIALIZE_INVESTOR_CONTACTS_SUCCESS';
export const MATERIALIZE_INVESTOR_CONTACTS_ERROR = 'MATERIALIZE_INVESTOR_CONTACTS_ERROR';

const request = (
  path,
  method,
  startType,
  successType,
  errorType,
  body,
  { query, successParams, errorParams, afterSuccess, afterError } = {}
) => ({
  meta: {},
  [CALL_API]: {
    method,
    startType,
    successType,
    errorType,
    body,
    query,
    successParams,
    errorParams,
    afterSuccess,
    afterError,
    dislodging: true,
    path: joinPath('/resource/auth/ps4', path),
  },
});

// Request against a service on its own base rather than the legacy backend: an
// absolute path (no `/resource/auth/ps4` prefix). Auth is unchanged — the
// middleware attaches the same `X-Auth-Token`, and the service's gateway
// validates it and forwards `X-User-Id`.
const serviceRequest = (base, path, method, startType, successType, errorType, body) => ({
  meta: {},
  [CALL_API]: {
    method,
    startType,
    successType,
    errorType,
    body,
    base,
    dislodging: true,
    path,
  },
});

export const searchSubjectSuggestions = (q) =>
  request(
    'property/suggestionsnew',
    'get',
    SEARCH_SUBJECT_SUGGESTIONS,
    SEARCH_SUBJECT_SUGGESTIONS_SUCCESS,
    SEARCH_SUBJECT_SUGGESTIONS_ERROR,
    null,
    { query: { q }, successParams: { query: q }, errorParams: { query: q } }
  );

export const setSubjectLabel = (label) => ({ type: SET_SUBJECT_LABEL, label });

export const setSubject = (subject) => ({ type: SET_SUBJECT, subject });

export const clearSubject = () => ({ type: CLEAR_SUBJECT });

export const setRecentSubjects = (items, loaded = false) => ({
  type: SET_RECENT_SUBJECTS,
  items,
  loaded,
});

export const setInvestorFilter = (name, value) => ({ type: SET_INVESTOR_FILTER, name, value });

export const resetInvestorFilters = () => ({ type: RESET_INVESTOR_FILTERS });

export const clearInvestorResults = () => ({ type: CLEAR_INVESTOR_RESULTS });

export const searchInvestors = (subject, filters) =>
  serviceRequest(
    BASE.PS_CORE_BASE_URL,
    INVESTOR_SEARCH_ENDPOINT,
    'post',
    SEARCH_INVESTORS,
    SEARCH_INVESTORS_SUCCESS,
    SEARCH_INVESTORS_ERROR,
    // Shape the body as the last step before the request is built, so
    // everything upstream stays in PropStream terms.
    toSearchRequest(subject, filters)
  );

// Add to Investor List (ATAI-180 / S10). One call does everything: materialize
// the selected investors as contacts (S7), create-or-resolve an INVESTOR-type
// group, and add the contacts to it with dedupe — see PR #1662 on
// propstream-be. `body` is `{ name?, groupId?, investors: [InvestorContactRequest] }`.
// Legacy propstream-be endpoint (not investor-service), hence the plain
// `request` helper rather than `serviceRequest`.
export const saveInvestorGroup = (body, afterSuccess, afterError) =>
  request(
    'user/contacts/new/investor-groups',
    'put',
    SAVE_INVESTOR_GROUP,
    SAVE_INVESTOR_GROUP_SUCCESS,
    SAVE_INVESTOR_GROUP_ERROR,
    body,
    { afterSuccess, afterError }
  );

// Remove a single investor from a single Investor List (ATAI-186), from the
// Investor Search / Profile bookmark — the counterpart to saveInvestorGroup.
// Reuses the existing generic `DELETE user/contacts/new` endpoint (the same
// one Contacts/index.js's drag-and-drop uses, via data/contacts/actions.js's
// deleteGroupContext) rather than a new endpoint, per the S10 build-spec.
//
// That endpoint deserializes into propstream-be's ContactSearchContext, which
// needs three things this thin call can supply directly instead of faking the
// Contacts page's full immutable "search context": `id` (the group id string,
// PREFIXED with the group type's first letter — 'I' for INVESTOR, per
// ContactGroup.Q_ALL_USER's `substr(g.type, 1, 1) || g.id` — stripped back off
// server-side by ContactSearchContext.getEntityId()), `type`, and `selection`
// (the one contact id to remove from that group). `contactId` only exists on
// `investor.savedGroups` entries once the propstream-core fix lands (S10/
// ATAI-186 BE) that adds it to the savedGroups query/DTO.
export const removeInvestorFromGroup = (groupId, groupName, contactId, afterSuccess, afterError) =>
  request(
    'user/contacts/new',
    'delete',
    REMOVE_INVESTOR_FROM_GROUP,
    REMOVE_INVESTOR_FROM_GROUP_SUCCESS,
    REMOVE_INVESTOR_FROM_GROUP_ERROR,
    { id: `I${groupId}`, name: groupName, type: 'INVESTOR', selection: [contactId] },
    { afterSuccess, afterError }
  );

// Skip Trace materialization (ATAI-115 / S9, backbone: ATAI-113 / S7). Turns
// selected search-result investors into `contacts` rows and returns their
// contact ids, so the existing Skip Trace modal (SkipTraceHandler) can open
// against them — no modal or endpoint changes there. Plain materialize only:
// unlike saveInvestorGroup, this does not touch Investor List membership.
// `body` is `{ investors: [InvestorContactRequest] }`; response is
// `{ results: [{ batchdataInvestorId, contactId, created }] }`.
export const materializeInvestorContacts = (body, afterSuccess, afterError) =>
  request(
    'user/contacts/materialize',
    'post',
    MATERIALIZE_INVESTOR_CONTACTS,
    MATERIALIZE_INVESTOR_CONTACTS_SUCCESS,
    MATERIALIZE_INVESTOR_CONTACTS_ERROR,
    body,
    { afterSuccess, afterError }
  );

// --- Investor Outreach subscription entitlement (ATAI-260 / S7) ------------------------------
// Cached in this slice (under `entitlement`) so the search route and the Account card read the
// primary holder's subscription state once instead of each re-calling the S2 cancel-preview — and
// re-showing a loader — on every visit.
export const FETCH_IO_ENTITLEMENT = 'FETCH_IO_ENTITLEMENT';
export const FETCH_IO_ENTITLEMENT_SUCCESS = 'FETCH_IO_ENTITLEMENT_SUCCESS';
export const FETCH_IO_ENTITLEMENT_ERROR = 'FETCH_IO_ENTITLEMENT_ERROR';
export const RESET_IO_ENTITLEMENT = 'RESET_IO_ENTITLEMENT';

// The thunk skips when the state is already loaded or a request is in flight, unless `force` (after
// a subscribe / unsubscribe / restore) asks for a fresh read. 200 -> subscribed (ACTIVE, or
// UNSUBSCRIBED + endDate when a cancellation is pending); 409 IO_NOT_SUBSCRIBED -> not subscribed;
// anything else -> error.
export const fetchInvestorOutreachEntitlement =
  (userId, { force = false } = {}) =>
  async (dispatch, getState) => {
    if (!userId) return;

    const entitlement = getState().get('investors').get('entitlement');
    if (!force && (entitlement.get('loading') || entitlement.get('loaded'))) return;

    dispatch({ type: FETCH_IO_ENTITLEMENT });

    const res = await getCancelPreview(userId);
    if (res.ok) {
      const pending = Boolean(res.data?.alreadyPending);
      dispatch({
        type: FETCH_IO_ENTITLEMENT_SUCCESS,
        entitled: true,
        status: pending ? 'UNSUBSCRIBED' : 'ACTIVE',
        endDate: pending ? res.data?.endDate || null : null,
        // Set by the backend when Connect's cancellation took Investor Outreach with it.
        connectCancelledWithIo: pending && Boolean(res.data?.connectCancelledWithIo),
        endedDate: null,
      });
    } else if (res.errorCode === 'IO_NOT_SUBSCRIBED') {
      dispatch({
        type: FETCH_IO_ENTITLEMENT_SUCCESS,
        entitled: false,
        status: null,
        endDate: null,
        connectCancelledWithIo: false,
        // The 409 body may say when a past subscription ended; that drives the grey ended banner.
        endedDate: res.data?.endedDate || res.data?.endDate || null,
      });
    } else {
      dispatch({ type: FETCH_IO_ENTITLEMENT_ERROR, error: res.message || true });
    }
  };

export const resetInvestorOutreachEntitlement = () => ({ type: RESET_IO_ENTITLEMENT });
